import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ASSET_TYPES, ASSET_LABELS, STATUSES } from '../utils';
import EmployeePicker, { employeeName } from './EmployeePicker';

export default function AddAssetModal({ onClose, onSuccess }) {
  const { withAuth } = useAuth();
  const [locations, setLocations] = useState([]);
  const [employeeName_, setEmployeeName] = useState('');
  const [form, setForm] = useState({
    assetType: 'laptop', displayName: '', serialNumber: '', make: '',
    model: '', status: 'in_storage', locationId: '', assignedToEmployeeId: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    withAuth(async () => {
      const locRes = await api.getLocations();
      setLocations(locRes.data || []);
    }).catch(() => setError('Failed to load locations'));
  }, [withAuth]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit() {
    if (!form.displayName || !form.serialNumber) {
      setError('Name and serial number are required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await withAuth(() => api.createAsset({
        ...form,
        locationId: form.locationId || null,
        assignedToEmployeeId: form.assignedToEmployeeId || null,
      }));
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  }

  // No click-outside-to-close: a stray backdrop click would discard typed work.
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <h3>Add New Asset</h3>

        <div className="form-row">
          <div className="form-group">
            <label>Device Type</label>
            <select value={form.assetType} onChange={e => upd('assetType', e.target.value)}>
              {ASSET_TYPES.map(t => <option key={t} value={t}>{ASSET_LABELS[t]}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={form.status} onChange={e => upd('status', e.target.value)}>
              {STATUSES.filter(s => ['active','in_storage'].includes(s.value)).map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Display Name *</label>
          <input value={form.displayName} onChange={e => upd('displayName', e.target.value)} placeholder="e.g. iPhone 15 Pro" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Serial Number *</label>
            <input value={form.serialNumber} onChange={e => upd('serialNumber', e.target.value)} placeholder="SN-XXXXXXXXX" />
          </div>
          <div className="form-group">
            <label>Model</label>
            <input value={form.model} onChange={e => upd('model', e.target.value)} placeholder="e.g. Dell Latitude 5540" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Make / Manufacturer</label>
            <input value={form.make} onChange={e => upd('make', e.target.value)} placeholder="e.g. Apple, Dell, Kyocera" />
          </div>
          <div className="form-group">
            <label>Location</label>
            <select value={form.locationId} onChange={e => upd('locationId', e.target.value)}>
              <option value="">— Select Location —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Assign To Employee</label>
          {/* Was a plain <select> fed by getEmployees({ limit: 500 }): anyone
              past the first 500 could not be assigned, with nothing to say so. */}
          <EmployeePicker
            value={form.assignedToEmployeeId}
            selectedName={employeeName_}
            onChange={emp => { upd('assignedToEmployeeId', emp?.id || ''); setEmployeeName(employeeName(emp)); }}
          />
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Optional notes…" />
        </div>

        {error && <div style={{ color: 'var(--red)', fontSize: 14, marginBottom: 12 }}>{error}</div>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Adding…' : 'Add Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}
